/**
 * /board — the pairing screen a tablet sits on before it becomes a scorer.
 *
 * ONE flat URL, with no venue in it. Whoever sets a tablet up types
 * `thehopdarts.co.uk/board` and nothing else; guessing or being told a venue
 * slug is exactly the friction this page exists to remove. The venue is
 * decided by WHO SCANS THE CODE, not by what was typed — an admin's account
 * already knows which venues they run.
 *
 * The code this page shows IS the pairing token the scorer later presents to
 * the room. There is no translation step, which is what lets the whole flow
 * reuse TournamentRoom's existing pair protocol unchanged.
 *
 * WHAT IS STORED, AND WHAT IS NOT. On a successful pairing this writes
 * hd_board_session to localStorage: a token, and the ids needed to open the
 * right socket. That is CONNECTION CONFIG — how to reach the room. It is never
 * game state. Match state is read from the room on every load, which is what
 * makes a reconnect a reconnect rather than a restart (V2-CARRYOVER.md §11,
 * where V1's scorer rebuilt itself from localStorage and a dropped tablet came
 * back looking like a fresh match).
 *
 * The QR is rendered from a bundled library, not fetched as an image from a QR
 * service. V1 used api.qrserver.com, which fails exactly when it is needed —
 * the uplink is down and someone is setting up a tablet — and hands a live
 * pairing URL to a third party on the way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';

import { apiGet, apiPost } from '../lib/api.js';

export const BOARD_SESSION_KEY = 'hd_board_session';

/** Fast enough to feel instant on the tablet, slow enough to be free. */
const POLL_MS = 2000;

export function readBoardSession() {
  try {
    const raw = localStorage.getItem(BOARD_SESSION_KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw);
    // Every field is needed to open the socket. A partial object is a storage
    // format that has moved on; treat it as unpaired rather than half-trusting
    // it and failing further in.
    if (!saved?.token || !saved?.venueSlug || !saved?.venueId || !saved?.tournamentId) {
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

export default function Board() {
  const navigate = useNavigate();

  const [code, setCode]   = useState(null);
  const [qr, setQr]       = useState(null);
  const [error, setError] = useState(null);
  const [paired, setPaired] = useState(null);

  // Survives the poll's closure so a code replaced mid-flight is not polled
  // for after it has been superseded.
  const codeRef = useRef(null);

  const pairUrl = code ? `${window.location.origin}/pair/${code}` : null;

  /** Ask for a fresh code and draw it. Also the recovery path from expiry. */
  const mintCode = useCallback(async () => {
    setError(null);
    setQr(null);

    const { data, error: err } = await apiPost('/api/board/code');

    if (err || !data?.code) {
      setCode(null);
      codeRef.current = null;
      setError(err ?? 'Could not reach the server');
      return;
    }

    codeRef.current = data.code;
    setCode(data.code);
  }, []);

  // A tablet that has already been paired belongs on its scorer, not here.
  // Checked before anything is minted so a reload during a match does not
  // flash a pairing screen at the room.
  useEffect(() => {
    const session = readBoardSession();
    if (session) {
      navigate(`/venues/${session.venueSlug}/scorer`, { replace: true });
      return;
    }
    mintCode();
  }, [navigate, mintCode]);

  // Drawn to a data URL rather than a canvas ref: it survives a re-render
  // without redrawing, and scales with CSS on whatever the tablet's screen is.
  useEffect(() => {
    if (!pairUrl) return;

    let live = true;

    QRCode.toDataURL(pairUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 512,
      // Pure black on pure white. A themed QR is a QR that some phones will
      // not read across a dim room.
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => { if (live) setQr(url); })
      .catch(() => { if (live) setError('Could not render the pairing code'); });

    return () => { live = false; };
  }, [pairUrl]);

  /**
   * A beat on the confirmation so whoever set the tablet up sees which board it
   * landed on, then hand over to the scorer.
   *
   * ITS OWN EFFECT, not a setTimeout inside the poll. Setting `paired` re-runs
   * the poll effect below — `paired` is in its dependency list — which tears
   * down the closure the timer was guarding itself with, so the redirect
   * checked a `live` flag that was already false and silently never fired. The
   * tablet paired and then sat on the confirmation screen for ever.
   */
  useEffect(() => {
    if (!paired) return undefined;

    const timer = setTimeout(
      () => navigate(`/venues/${paired.venueSlug}/scorer`, { replace: true }),
      1200
    );

    return () => clearTimeout(timer);
  }, [paired, navigate]);

  // Poll for the admin's half of the handshake.
  useEffect(() => {
    if (!code || paired) return undefined;

    let live = true;
    let timer = null;

    const tick = async () => {
      const { data, status } = await apiGet(`/api/board/pairing-status?code=${code}`);

      if (!live || codeRef.current !== code) return;

      // The code aged out before anyone scanned it. Silently replace it — a
      // ten-minute QR sitting on a wall all evening is the point, and an error
      // message on a pub wall is not.
      if (status === 404) {
        mintCode();
        return;
      }

      if (data?.status === 'claimed') {
        const session = {
          token:        code,
          venueId:      data.venueId,
          venueSlug:    data.venueSlug,
          tournamentId: data.tournamentId,
          boardNumber:  data.boardNumber,
        };

        try {
          localStorage.setItem(BOARD_SESSION_KEY, JSON.stringify(session));
        } catch {
          // Private browsing, or a full quota. The pairing itself is fine —
          // the room holds it — but this device cannot remember it across a
          // reload, and saying so beats a scorer that silently unpairs later.
          setError('This device cannot store its pairing. Check browser settings.');
          return;
        }

        setPaired({ ...session, venueName: data.venueName });
        return;
      }

      timer = setTimeout(tick, POLL_MS);
    };

    timer = setTimeout(tick, POLL_MS);

    return () => { live = false; clearTimeout(timer); };
  }, [code, paired, navigate, mintCode]);

  if (paired) {
    return (
      <div className="hd-fullscreen hd-board-pair">
        <div className="hd-board-pair-inner">
          <div className="hd-board-pair-tick">✓</div>
          <h1 className="hd-board-pair-board">Board {paired.boardNumber}</h1>
          <p className="hd-board-pair-lede">
            Paired{paired.venueName ? ` — ${paired.venueName}` : ''}. Starting the scorer…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="hd-fullscreen hd-board-pair">
      <div className="hd-board-pair-inner">
        <div className="hd-brand" style={{ padding: 0, fontSize: 20 }}>HOPDARTS</div>

        <h1 className="hd-board-pair-title">Pair this board</h1>
        <p className="hd-board-pair-lede">
          Scan with your phone and sign in as a venue admin.
        </p>

        <div className="hd-board-pair-qr">
          {qr
            ? <img src={qr} alt="Scan to pair this board" />
            : <div className="hd-empty">{error ? 'Unavailable' : 'Preparing…'}</div>}
        </div>

        {code && (
          <>
            {/* The typed fallback. A camera that will not focus across a dim
                room is common enough that the code has to be readable too —
                which is why it is drawn from an alphabet with no I, L, O or U. */}
            <div className="hd-board-pair-code">{code}</div>
            <p className="hd-meta">
              Or go to {window.location.host}/pair and enter this code.
            </p>
          </>
        )}

        {error && (
          <div className="hd-stack" style={{ alignItems: 'center', gap: 8 }}>
            <div className="hd-badge hd-badge-danger" role="alert">{error}</div>
            <button type="button" className="hd-btn hd-btn-secondary" onClick={mintCode}>
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
